import type { FunctionComponent, RefObject } from 'preact';
import { useMemo } from 'preact/hooks';

import type { IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys';
import type { Params as AutoSyncVisualizationParams } from 'Components/fitmentSearch/autoSyncVisualization';
import type { FacetToggleConfig, Params as FacetToggleParams } from 'Components/smallComponents/facetToggle';
import type { Params as SelectParams } from 'Components/smallComponents/select';
import type { RepeaterFunctionInvoker, TemplateFunction, TemplateFunctionInvoker } from 'Components/types';
import {
  createFitmentSearchResponseFacetsSelector,
  isVehicleSelectedSelector,
} from 'Core/selectors/fitmentSearch/index';
import { type Selector, useSelector } from 'Core/store';
import type { FacetCollection } from 'Models/index';
import { createFacetCollectionSelector } from 'Modules/selectors';
import type { CloneSafeProps } from 'Utils/components';

import type { VehicleTemplateData } from '../types';
import ActualDropdowns from './actualDropdowns';
import StubDropdowns from './stubDropdowns';
import { type Params as UseCollapsingParams, useCollapsing } from './useCollapsing';
import type { LayoutType } from './useLayoutState';

export type Params = {
  name: string;
  selects: RepeaterFunctionInvoker<SelectParams>;
  vehicleListSize?: number;
  allowToSetVehicle: boolean;
  allowToDiscardVehicle: boolean;
  aboutToChange: boolean;
  selectVehicle: () => void;
  openDialog: () => void;
  changeVehicle: () => void;
  facetToggle: TemplateFunctionInvoker<FacetToggleParams>;
  discardVehicle: () => void;
  discardExtraFields: () => void;
  discardFields: (fields: string[]) => void;
  fitmentSearchTitle: string;
  template: 'active';
  isVehicleSelected: boolean;
  isVehicleSelectionForced: boolean;
  selection: Record<string, string>;
  isAllFieldsSelected: boolean;
  autoSyncVisualization?: TemplateFunctionInvoker<AutoSyncVisualizationParams>;
} & VehicleTemplateData &
  UseCollapsingParams;

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  appendedClasses: CloneSafeProps['appendedClasses'];
  aboutToChange: boolean;
  fields?: string[];
  initCollapsed: boolean;
  isolatedKey: IsolatedKey;
  layoutState: LayoutType;
  onSelectVehicle: () => void;
  onChangeVehicle: () => void;
  openDialog: () => void;
  rootRef: RefObject<HTMLElement>;
  useNativeDropdown?: boolean;
  facetToggleConfig?: FacetToggleConfig;
  useSearchableDropdown?: boolean;
  autoOpenDropdowns?: 'all' | 'next';
  globalVehicleDiscardEnabled?: boolean;
  redirectUrl?: string;
  isAutoVehicleSelectionDisabled?: boolean;
  doNotRedirectOnVehicleSelect?: boolean;
  isVehicleSelectionForced: boolean;
  isVehicleSelectionIsolated?: boolean;
  autoSyncVisualization?: TemplateFunctionInvoker<AutoSyncVisualizationParams>;
};

export type InnerProps = Omit<Props, 'initCollapsed' | 'layoutState'> & {
  collapsing: UseCollapsingParams;
  facets: FacetCollection;
  isVehicleSelected: boolean;
  isVehicleSelectionIsolated?: boolean;
};

const VehicleDropdowns: FunctionComponent<Props> = ({ initCollapsed, layoutState, ...props }) => {
  const isVehicleSelected = useSelector(isVehicleSelectedSelector);
  const facets = useFacets(props.isolatedKey, props.fields);

  const collapsing = useCollapsing(
    props.rootRef,
    isVehicleSelected,
    layoutState,
    initCollapsed,
    props.aboutToChange,
  );

  const extraProps = { collapsing, facets, isVehicleSelected };

  const Component = facets.length ? ActualDropdowns : StubDropdowns;
  return <Component {...props} {...extraProps} />;
};

function useFacets(isolatedKey: IsolatedKey, fields?: string[]) {
  return useSelector(
    useMemo(
      (): Selector<FacetCollection> =>
        fields
          ? createFacetCollectionSelector(
              createFitmentSearchResponseFacetsSelector(isolatedKey),
              (facets: FacetCollection) => facets.filter((facet) => fields.includes(facet.field)),
            )
          : createFitmentSearchResponseFacetsSelector(isolatedKey),
      [fields, isolatedKey],
    ),
  );
}

export default VehicleDropdowns;
